import { getImageExtension } from "client/utils/getImageExtension";
import { useState } from "react";
import { setAllImagesCompressionSettings } from "store/features/compress/actions";
import { compressSelector } from "store/features/compress/selectors/compressSelector";
import { CompressImage } from "store/features/compress/types";
import { getCompressionSettings } from "store/features/compress/utils/getCompressionSettings";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { useCompress } from "../../useCompress";

export const useApplyButtons = () => {
    const dispatch = useAppDispatch();
    const { compressImage } = useCompress();
    const { images } = useAppSelector(compressSelector);
    const isApplyToAllDisabled = images.some((image) => image.state === "compressing" && !image.error);
    const [isMaxFileSizeConfirmationModalOpen, setIsMaxFileSizeConfirmationModalOpen] = useState(false);

    const handleApply = (image: CompressImage) => {
        if (!image.importTask) return;
        compressImage(
            image,
            image.importTask,
            getCompressionSettings(getImageExtension(image.file.file), image.compressionSettings),
            image.file.file.name,
        );
    };

    const shouldShowMaxFileSizeConfirmationModal = (image: CompressImage) => {
        const allImages = store.getState().compress.images;
        return (
            image.compressionSettings.type === "max_file_size" &&
            image.compressionSettings.maxFileSize.value > 0 &&
            !isMaxFileSizeConfirmationModalOpen &&
            allImages.some(
                (image) =>
                    !(getImageExtension(image.file.file) === "jpg" || getImageExtension(image.file.file) === "jpeg"),
            )
        );
    };
    const handleApplyToAll = (image: CompressImage) => {
        if (isApplyToAllDisabled) return;
        const allImages = store.getState().compress.images;

        if (shouldShowMaxFileSizeConfirmationModal(image)) {
            setIsMaxFileSizeConfirmationModalOpen(true);
            return;
        }

        const images = allImages.filter((_image) => {
            if (image.compressionSettings.type === "max_file_size") {
                const ext = getImageExtension(_image.file.file);
                return ext === "jpg" || ext === "jpeg";
            }
            return true;
        });
        dispatch(setAllImagesCompressionSettings(image.compressionSettings));
        images.forEach((_image) => {
            if (!_image.importTask) return;
            compressImage(
                _image,
                _image.importTask,
                getCompressionSettings(getImageExtension(_image.file.file), image.compressionSettings),
                _image.file.file.name,
            );
        });
    };

    const handleReset = () => {
        if (isApplyToAllDisabled) return;
        const allImages = store.getState().compress.images;
        const compressionSettings = store.getState().compress.compressionSettings;

        if (
            compressionSettings.type === "max_file_size" &&
            compressionSettings.maxFileSize.value > 0 &&
            !isMaxFileSizeConfirmationModalOpen &&
            allImages.some(
                (image) =>
                    !(getImageExtension(image.file.file) === "jpg" || getImageExtension(image.file.file) === "jpeg"),
            )
        ) {
            setIsMaxFileSizeConfirmationModalOpen(true);
            return;
        }

        const images = allImages.filter((_image) => {
            if (compressionSettings.type === "max_file_size") {
                const ext = getImageExtension(_image.file.file);
                return ext === "jpg" || ext === "jpeg";
            }
            return true;
        });
        dispatch(setAllImagesCompressionSettings(compressionSettings));
        images.forEach((_image) => {
            if (!_image.importTask) return;
            compressImage(
                _image,
                _image.importTask,
                getCompressionSettings(getImageExtension(_image.file.file), compressionSettings),
                _image.file.file.name,
            );
        });
    };

    const onQualityChangeApply = () => {
        const selectedImageId = store.getState().compress.selectedImageId;
        const image = store.getState().compress.images.find((_i) => _i.id === selectedImageId);
        if (!image || !image.importTask) return;
        compressImage(
            image,
            image.importTask,
            getCompressionSettings(getImageExtension(image.file.file), image.compressionSettings),
            image.file.file.name,
        );
    };

    return {
        handleApply,
        handleApplyToAll,
        isApplyToAllDisabled,
        isMaxFileSizeConfirmationModalOpen,
        setIsMaxFileSizeConfirmationModalOpen,
        onQualityChangeApply,
        handleReset,
    };
};
